<template>
    <button
        ref="buttonRef"
        class="group relative flex flex-col"
        type="button"
        :disabled="disabled && !clickableWhenDisabled"
    >
        <span :class="widthClass">
            <span
                class="relative block overflow-hidden"
                :class="[widthClass, aspectClass, roundedCss, heightClass]"
            >
                <img
                    v-if="imageUrl && !useStoryBlokImage"
                    :src="imageUrl"
                    class="w-full object-cover transition-opacity"
                    :alt="label"
                    :class="[{ 'opacity-75': disabled }, imageClasses]"
                />
                <StoryblokImage
                    v-if="imageUrl && useStoryBlokImage"
                    :src="imageUrl"
                    class="w-full object-cover transition-opacity"
                    :alt="label"
                    :quality="95"
                    sizes="180px md:280px"
                    :class="[{ 'opacity-75': disabled }, imageClasses]"
                />
                <svg
                    xmlns="http://www.w3.org/2000/svg"
                    preserveAspectRatio="none"
                    viewBox="0 0 100 100"
                    class="pointer-events-none absolute inset-0 h-full w-full text-grind-200 transition-opacity"
                    :class="disabled ? '' : 'opacity-0'"
                >
                    <line
                        x1="0"
                        y1="100"
                        x2="100"
                        y2="0"
                        stroke="currentColor"
                        vector-effect="non-scaling-stroke"
                    />
                </svg>

                <span
                    class="absolute inset-0 ring-inset transition"
                    :class="[
                        roundedCss,
                        {
                            'group-hover:ring-3 group-hover:ring-grind':
                                clickableWhenDisabled || !disabled,
                            'ring-3 ring-grind': !errored && selected,
                            'ring-1 ring-grind-200 transition group-hover:ring-3 group-hover:ring-grind':
                                !errored && !selected,
                            'ring-2 ring-error': errored,
                        },
                    ]"
                ></span>
            </span>

            <span class="mt-1.5 flex flex-col items-center" :class="labelClass">
                <span
                    class="transition group-hover:text-grind"
                    :class="{
                        'font-bold': selected,
                        'font-semibold text-error': errored,
                    }"
                >
                    {{ label }}
                </span>
            </span>

            <span
                v-if="subLabel"
                :class="[
                    subLabelClass,
                    {
                        'font-bold': selected,
                        'font-semibold text-error': errored,
                    },
                ]"
            >
                {{ subLabel }}
            </span>

            <Tooltip
                v-if="tooltip && (!disabled || clickableWhenDisabled)"
                class="absolute left-[50%] top-0 opacity-0 transition-opacity group-hover:opacity-100"
            >
                {{ tooltip }}
            </Tooltip>
        </span>
    </button>
</template>

<script setup lang="ts">
import Tooltip from '~/components/Tooltip.vue';
import { useElementSize } from '@vueuse/core';

const buttonRef = ref(null);
const { height } = useElementSize(buttonRef);

withDefaults(
    defineProps<{
        label: string;
        subLabel?: string;
        imageUrl: string;
        selected: boolean;
        disabled?: boolean;
        clickableWhenDisabled?: boolean;
        errored?: boolean;
        tooltip?: string;
        widthClass?: string;
        heightClass?: string;
        aspectClass?: string;
        imageClasses?: string;
        labelClass?: string;
        subLabelClass?: string;
        useStoryBlokImage?: boolean;
    }>(),
    {
        subLabel: undefined,
        disabled: false,
        clickableWhenDisabled: false,
        errored: false,
        tooltip: undefined,
        widthClass: 'w-full sm:w-32',
        heightClass: '',
        aspectClass: 'aspect-square',
        imageClasses: 'w-full',
        labelClass: undefined,
        subLabelClass:
            'text-2xs xs:text-xs leading-none xs:leading-none text-grind/80',
        useStoryBlokImage: false,
    },
);

const roundedCss = computed(() => {
    return height.value >= 50 ? 'rounded-xl' : 'rounded-md';
});
</script>
